import * as React from 'react';
import { addMonths, isAfter, isBefore, isSameDay } from 'date-fns';
import { ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import { customTheme } from '../../libs';

interface DateRangeCalendarProps {
  endDate: Date | null;
  onSelectDate: (date: Date) => void;
  startDate: Date | null;
}

// Two-month inline calendar with start/end range selection. The free
// @mui/x-date-pickers package only ships single-date `DateCalendar`, so we
// compose two of them and decorate the day slot with shared range awareness.
//
// Selection semantics (mirrors react-dates' DateRangePicker):
//   - If no start date OR both dates already set → set start, clear end
//   - If start is set and clicked date is before it → replace start
//   - Otherwise → set end
//
// Month navigation: the two months are kept consecutive. Clicking either
// calendar's nav arrows updates the shared `leftMonth` state so the right
// calendar always shows leftMonth + 1.
export const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({ endDate, onSelectDate, startDate }) => {
  const [leftMonth, setLeftMonth] = React.useState<Date>(startDate || new Date());

  const RangeDay = React.useMemo(() => {
    return function Day(dayProps: PickersDayProps<Date>) {
      const { day } = dayProps;
      const isStart = !!(startDate && isSameDay(day, startDate));
      const isEnd = !!(endDate && isSameDay(day, endDate));
      const inRange = !!(
        startDate
        && endDate
        && isAfter(day, startDate)
        && isBefore(day, endDate)
      );

      return (
        <PickersDay
          {...dayProps}
          selected={isStart || isEnd}
          sx={inRange ? {
            backgroundColor: 'rgba(6, 128, 107, 0.15)',
            borderRadius: 0,
          } : undefined}
        />
      );
    };
  }, [startDate, endDate]);

  // The right calendar's `referenceDate` is initial-only inside MUI's internal
  // state, so a key forces a remount whenever leftMonth changes. That keeps
  // the two months in lockstep regardless of which arrow the user clicks.
  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className="date-range-calendar">
          <DateCalendar
            className="date-range-calendar__month"
            onChange={onSelectDate}
            onMonthChange={setLeftMonth}
            referenceDate={leftMonth}
            slots={{ day: RangeDay }}
            value={null}
          />
          <DateCalendar
            className="date-range-calendar__month"
            key={`right-${leftMonth.toISOString()}`}
            onChange={onSelectDate}
            onMonthChange={(m: Date) => setLeftMonth(addMonths(m, -1))}
            referenceDate={addMonths(leftMonth, 1)}
            slots={{ day: RangeDay }}
            value={null}
          />
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateRangeCalendar;
